// เนื้อเรื่องแมลงสังคม: มด ปลวก ผึ้งพันธุ์ และต่อที่อยู่เป็นรัง
// บทบาทสุ่มตอนเกิด (role_queen / role_worker) ตัวงานไม่ออกลูกเอง คะแนนคือน้องที่ช่วยเลี้ยง (offspringCount)
// นางพญา: บินผสมพันธุ์ (mated_queen) → สร้างรัง (colony_founded) → วางไข่ไปตลอดชีวิต

import { TERMITE_FAMILIES } from "../../data/groups";
import type { GameEvent } from "../../engine/types";

const S = ["SOCIAL_INSECT"] as const;
const ANT_TERMITE = ["insect.ant_termite"];
const BEE_WASP = ["insect.bee_wasp"];

export const SOCIAL_INSECT_EVENTS: readonly GameEvent[] = [
  // ---------- ตัวอ่อนในรัง ----------
  {
    id: "social_larva_fed",
    archetypes: S,
    stages: ["larva"],
    weight: 10,
    text: "พี่เลี้ยงเดินผ่านมา ป้อนอาหารเหลวให้ตัวอ่อนทีละตัว",
    fact: "ตัวอ่อนของมดและผึ้งไม่มีขา ต้องพึ่งตัวงานป้อนอาหารและทำความสะอาดให้ทั้งหมด",
    choices: [
      { label: "ส่ายหัวส่งสัญญาณว่ายังหิว", outcomes: [{ chance: 1, text: "พี่เลี้ยงแวะกลับมาป้อนคุณอีกรอบ", effects: { fullness: 35 } }] },
      { label: "กินแล้วนอนนิ่งๆ", outcomes: [{ chance: 1, text: "คุณอิ่มพอดีและตัวโตขึ้นอีกนิด", effects: { fullness: 25, health: 5 } }] },
    ],
  },
  {
    id: "termite_nymph_fed",
    archetypes: S,
    stages: ["nymph"],
    weight: 10,
    text: "พี่ๆ ในรังเดินมาแบ่งอาหารที่ย่อยแล้วให้คุณ",
    fact: "ปลวกส่งต่ออาหารและจุลินทรีย์ที่ช่วยย่อยเนื้อไม้ให้กันทางปากและทางก้น ลูกปลวกจึงย่อยไม้ได้เหมือนพี่ๆ",
    choices: [
      { label: "รับอาหารจากพี่ๆ", outcomes: [{ chance: 1, text: "ท้องของคุณเต็มไปด้วยอาหารและจุลินทรีย์ที่ช่วยย่อยไม้", effects: { fullness: 35 } }] },
      { label: "ลองแทะเศษไม้เอง", outcomes: [{ chance: 1, text: "คุณแทะได้ไม่มาก แต่ก็เริ่มรู้วิธี", effects: { fullness: 20, health: 5 } }] },
    ],
  },
  {
    id: "social_royal_larva",
    archetypes: S,
    stages: ["larva", "nymph"],
    requires: { flags: ["role_queen"] },
    once: true,
    weight: 12,
    text: "คุณสังเกตว่าพี่เลี้ยงแวะมาป้อนอาหารคุณบ่อยกว่าตัวอื่นๆ",
    fact: "ผึ้งนางพญาเกิดจากไข่แบบเดียวกับผึ้งงาน แต่ได้กินนมผึ้งตลอดช่วงตัวอ่อน จึงตัวใหญ่และวางไข่ได้",
    choices: [
      { label: "กินทุกอย่างที่ได้รับ", outcomes: [{ chance: 1, text: "ร่างกายของคุณโตเร็วกว่าพี่น้องตัวอื่น", effects: { fullness: 40, health: 10 } }] },
    ],
  },
  {
    id: "social_larva_groomed",
    archetypes: S,
    stageTags: ["dependent"],
    weight: 8,
    text: "พี่เลี้ยงหลายตัวมารุมเลียทำความสะอาดตัวคุณ",
    fact: "มดงานเลียทำความสะอาดตัวอ่อนเพื่อกำจัดสปอร์เชื้อรา และมดหลายชนิดยังมีต่อมที่หลั่งสารฆ่าเชื้อด้วย",
    choices: [
      { label: "นอนนิ่งให้พี่ๆ ทำความสะอาด", outcomes: [{ chance: 1, text: "ตัวคุณสะอาดเอี่ยม ไม่มีเชื้อราเกาะเลย", effects: { health: 10, safety: 5 } }] },
      { label: "ขยับตัวขออาหารไปด้วย", outcomes: [{ chance: 1, text: "พี่เลี้ยงตัวหนึ่งป้อนอาหารให้คุณเพิ่ม", effects: { fullness: 20, health: 5 } }] },
    ],
  },
  {
    id: "social_brood_care",
    archetypes: S,
    stageTags: ["dormant"],
    weight: 10,
    text: "ตัวงานคาบคุณย้ายไปไว้ในห้องที่อุณหภูมิกำลังพอดี",
    fact: "ตัวงานย้ายไข่และดักแด้ขึ้นลงระหว่างห้องในรังตลอดวัน เพื่อให้ได้อุณหภูมิและความชื้นที่เหมาะสม",
    choices: [{ label: "รอเวลา", outcomes: [{ chance: 1, text: "ห้องใหม่อบอุ่นและมีตัวงานเฝ้าอยู่ตลอด", effects: { health: 5, safety: 5 } }] }],
  },
  {
    id: "social_nest_flood",
    archetypes: S,
    stageTags: ["dependent"],
    weight: 4,
    text: "ฝนตกหนักทั้งคืน น้ำเริ่มซึมเข้ามาในห้องอนุบาล",
    choices: [
      {
        label: "รอให้พี่ๆ มาขนย้าย",
        outcomes: [
          { chance: 0.8, text: "ตัวงานรีบคาบคุณขึ้นไปไว้ห้องที่สูงกว่า", effects: { safety: 5 } },
          { chance: 0.2, text: "น้ำมาเร็วกว่าที่ตัวงานจะขนทัน คุณจมน้ำอยู่นาน", effects: { health: -30, cause: "disaster" } },
        ],
      },
      {
        label: "ดิ้นแรงๆ เรียกความสนใจ",
        outcomes: [
          { chance: 0.9, text: "พี่เลี้ยงเห็นคุณก่อนใคร และพาคุณหนีน้ำ", effects: { safety: 5, fullness: -5 } },
          { chance: 0.1, text: "คุณกลิ้งตกลงไปในแอ่งน้ำ", effects: { health: -25, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "social_brood_raid",
    archetypes: S,
    stageTags: ["dependent"],
    requires: { subgroups: ANT_TERMITE },
    weight: 3,
    text: "มดจากรังอื่นบุกเข้ามา พวกมันมาเพื่อขโมยตัวอ่อน",
    choices: [
      {
        label: "ซุกตัวอยู่ในกองตัวอ่อน",
        outcomes: [
          { chance: 0.8, text: "พี่ๆ ต่อสู้ขับไล่ผู้บุกรุกออกไปได้", effects: { safety: -5 } },
          { chance: 0.2, text: "ผู้บุกรุกคาบคุณออกไปจากรัง", effects: { death: "predator" } },
        ],
      },
    ],
  },

  // ---------- ตัวงาน ----------
  {
    id: "worker_first_day",
    opening: true,
    archetypes: S,
    stages: ["adult"],
    requires: { flags: ["role_worker"] },
    weight: 10,
    text: "คุณเป็นตัวงาน งานแรกของตัวงานอายุน้อยคือดูแลไข่และตัวอ่อนในรัง",
    fact: "มดงานและผึ้งงานที่อายุน้อยมักทำงานในรัง เช่น เลี้ยงตัวอ่อน แล้วค่อยออกไปหาอาหารนอกรังเมื่ออายุมากขึ้น",
    choices: [
      { label: "ทำความสะอาดไข่", outcomes: [{ chance: 1, text: "คุณเลียทำความสะอาดไข่ไม่ให้ขึ้นรา น้องๆ {n} ตัวฟักออกมาอย่างแข็งแรง", effects: { offspringCount: 10 } }] },
      { label: "ป้อนอาหารตัวอ่อน", outcomes: [{ chance: 1, text: "คุณป้อนอาหารจนน้องๆ {n} ตัวโตขึ้น แม้ตัวเองจะเหนื่อย", effects: { offspringCount: 15, fullness: -5 } }] },
    ],
  },
  {
    id: "worker_nurse",
    archetypes: S,
    stages: ["adult"],
    requires: { flags: ["role_worker"] },
    weight: 10,
    text: "ห้องอนุบาลเต็มไปด้วยตัวอ่อนที่หิวโหย",
    choices: [
      { label: "ป้อนอาหารทีละตัว", outcomes: [{ chance: 1, text: "น้องๆ อีก {n} ตัวโตขึ้นเพราะคุณ", effects: { offspringCount: 20, fullness: -10 } }] },
      { label: "ย้ายตัวอ่อนไปห้องที่อุ่นกว่า", outcomes: [{ chance: 1, text: "ห้องใหม่อุ่นกำลังดี น้องๆ {n} ตัวโตเร็วขึ้น", effects: { offspringCount: 10, safety: 5 } }] },
    ],
  },
  {
    id: "worker_forage",
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: ANT_TERMITE, flags: ["role_worker"] },
    weight: 10,
    text: "คุณออกไปหาอาหารนอกรัง",
    fact: "มดทิ้งสารเคมีที่เรียกว่าฟีโรโมนไว้ตามทาง ยิ่งมีมดเดินผ่านมาก กลิ่นยิ่งแรง ทั้งรังจึงหาเส้นทางที่ดีที่สุดได้",
    choices: [
      {
        label: "เดินตามทางกลิ่นที่พี่ๆ ทิ้งไว้",
        outcomes: [
          { chance: 0.85, text: "ทางกลิ่นพาคุณไปถึงอาหาร คุณขนกลับมาเลี้ยงน้องได้อีก {n} ตัว", effects: { fullness: 30, offspringCount: 5 } },
          { chance: 0.15, text: "จิ้งจกดักรออยู่ปลายทาง คุณหนีมาได้อย่างหวุดหวิด", effects: { health: -25, cause: "predator" } },
        ],
      },
      {
        label: "บุกเบิกทางใหม่",
        outcomes: [
          { chance: 0.5, text: "คุณเจอแหล่งอาหารใหม่ แล้วทิ้งกลิ่นนำทางให้ทั้งรัง น้องๆ {n} ตัวได้กินอิ่ม", effects: { fullness: 20, offspringCount: 15 } },
          { chance: 0.4, text: "คุณหลงทาง กว่าจะกลับรังได้ก็เกือบหมดแรง", effects: { fullness: -20 } },
          { chance: 0.1, text: "แมงมุมกระโดดตะครุบคุณ", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "worker_defend",
    archetypes: S,
    stages: ["adult"],
    requires: { flags: ["role_worker"] },
    weight: 6,
    text: "ผู้บุกรุกตัวใหญ่กำลังพังทางเข้ารัง",
    choices: [
      {
        label: "พุ่งเข้าใส่ ปกป้องรัง",
        outcomes: [
          { chance: 0.55, text: "คุณกับพี่น้องรุมจนผู้บุกรุกถอยหนี น้องๆ {n} ตัวในรังปลอดภัย", effects: { safety: 10, offspringCount: 5 } },
          { chance: 0.3, text: "คุณบาดเจ็บ แต่รังปลอดภัย", effects: { health: -25, addFlags: ["injured"], cause: "predator" } },
          { chance: 0.15, text: "คุณสู้จนตัวตาย แต่รังและน้องๆ รอดปลอดภัย", effects: { death: "sacrifice" } },
        ],
      },
      {
        label: "ถอยไปเฝ้าห้องตัวอ่อน",
        outcomes: [
          { chance: 0.8, text: "คุณขวางทางเข้าห้องอนุบาลไว้ น้องๆ {n} ตัวไม่เป็นอะไร", effects: { offspringCount: 5, safety: 5 } },
          { chance: 0.2, text: "ผู้บุกรุกตามมาถึงห้องอนุบาล คุณบาดเจ็บ", effects: { health: -15, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "pangolin_raid",
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: ANT_TERMITE },
    weight: 3,
    text: "ลิ่นตัวหนึ่งกำลังตะกุยรังของคุณ ลิ้นยาวเหนียวแผล็บเข้ามาในอุโมงค์",
    fact: "ลิ่นไม่มีฟัน ใช้ลิ้นยาวเหนียวเลียมดและปลวกกินเป็นจำนวนมหาศาล และปิดรูจมูกกับหูได้เพื่อกันถูกกัด",
    choices: [
      {
        label: "หนีลึกลงไปในรัง",
        outcomes: [
          { chance: 0.9, text: "ลิ่นกินไปไม่น้อย แต่คุณหลบอยู่ในห้องลึกได้ทัน", effects: { safety: -10 } },
          { chance: 0.1, text: "ลิ้นยาวเหนียวตามมาถึงตัวคุณ", effects: { death: "predator" } },
        ],
      },
      {
        label: "ตั้งหลักสู้ที่ทางเข้า",
        outcomes: [
          { chance: 0.7, text: "ลิ่นโดนรุมกัดนานจนรำคาญ แล้วเดินจากไป", effects: { safety: -5 } },
          { chance: 0.3, text: "คุณติดลิ้นของมันไปพร้อมพี่น้องอีกนับร้อย", effects: { death: "predator" } },
        ],
      },
    ],
  },

  // ---------- มด ----------
  {
    id: "ant_aphid",
    archetypes: S,
    stages: ["adult"],
    requires: { families: ["Formicidae"], flags: ["role_worker"] },
    weight: 5,
    text: "เพลี้ยฝูงหนึ่งเกาะดูดน้ำเลี้ยงอยู่บนกิ่งไม้ ก้นของพวกมันมีหยดน้ำหวานใสๆ",
    fact: "มดหลายชนิดเลี้ยงเพลี้ยไว้คล้ายฟาร์ม คอยปกป้องจากศัตรูเพื่อแลกกับน้ำหวานที่เพลี้ยขับออกมา",
    choices: [
      { label: "เฝ้าฝูงเพลี้ย แลกกับน้ำหวาน", outcomes: [{ chance: 1, text: "คุณไล่ด้วงเต่าออกไป แล้วได้น้ำหวานกลับไปเลี้ยงน้องๆ {n} ตัว", effects: { fullness: 30, offspringCount: 5 } }] },
      { label: "จับเพลี้ยกินเลย", outcomes: [{ chance: 1, text: "คุณได้โปรตีนมื้อใหญ่ แต่ฟาร์มก็เล็กลง", effects: { fullness: 25 } }] },
    ],
  },
  {
    id: "ant_share",
    archetypes: S,
    stages: ["adult"],
    requires: { families: ["Formicidae"], flags: ["role_worker"] },
    weight: 5,
    text: "พี่น้องหลายตัวในรังยังไม่ได้กินอะไร พวกมันเอาหนวดมาแตะปากคุณ",
    fact: "มดมีกระเพาะพิเศษไว้เก็บอาหารเหลว แล้วสำรอกแบ่งให้พี่น้องในรังทีละตัว",
    choices: [
      { label: "สำรอกอาหารแบ่งให้", outcomes: [{ chance: 1, text: "อาหารในท้องคุณกระจายไปทั่วรัง เลี้ยงน้องๆ ได้อีก {n} ตัว", effects: { fullness: -10, offspringCount: 8 } }] },
      { label: "เก็บไว้กินเอง", outcomes: [{ chance: 1, text: "คุณอิ่ม แต่พี่น้องต้องรอตัวอื่นแทน", effects: { fullness: 10 } }] },
    ],
  },
  {
    id: "ant_nest_expand",
    archetypes: S,
    stages: ["adult"],
    requires: { families: ["Formicidae"], flags: ["role_worker"] },
    weight: 4,
    text: "รังของคุณแน่นจนไม่มีที่ให้ตัวอ่อนแล้ว ทั้งรังต้องขยายห้องใหม่",
    fact: "มดแดงสร้างรังจากใบไม้สด โดยตัวงานช่วยกันดึงใบไม้มาประกบ แล้วใช้ตัวอ่อนที่ปล่อยใยไหมเย็บใบไม้ให้ติดกัน",
    choices: [
      {
        label: "ช่วยกันสร้างห้องใหม่",
        outcomes: [
          { chance: 0.85, text: "ห้องใหม่เสร็จทันเวลา มีที่ให้น้องๆ อีก {n} ตัว", effects: { offspringCount: 10, fullness: -10 } },
          { chance: 0.15, text: "ผนังห้องที่ยังไม่แห้งพังทับคุณ", effects: { health: -20, cause: "accident" } },
        ],
      },
      { label: "ขนตัวอ่อนย้ายไปห้องใหม่", outcomes: [{ chance: 1, text: "คุณขนน้องๆ {n} ตัวไปไว้ในห้องที่กว้างกว่า", effects: { offspringCount: 6 } }] },
    ],
  },

  // ---------- ปลวก ----------
  {
    id: "termite_wood",
    archetypes: S,
    stages: ["adult"],
    requires: { families: TERMITE_FAMILIES, flags: ["role_worker"] },
    weight: 7,
    text: "คุณแทะเนื้อไม้ผุอยู่ในความมืด",
    fact: "ปลวกย่อยเนื้อไม้ได้เพราะมีจุลินทรีย์ในลำไส้ช่วยย่อยเซลลูโลส",
    choices: [
      { label: "แทะต่อไปเรื่อยๆ", outcomes: [{ chance: 1, text: "ไม้ท่อนนี้เลี้ยงทั้งรังได้อีกนาน คุณขนกลับไปเลี้ยงน้องๆ {n} ตัว", effects: { fullness: 35, offspringCount: 5 } }] },
      {
        label: "ขุดอุโมงค์ไปหาไม้ท่อนใหม่",
        outcomes: [
          { chance: 0.7, text: "อุโมงค์ทะลุถึงเสาไม้ต้นใหญ่ อาหารพอเลี้ยงน้องๆ อีก {n} ตัว", effects: { fullness: 25, offspringCount: 10 } },
          { chance: 0.3, text: "อุโมงค์ทะลุออกไปเจอแสง มดที่เดินผ่านมาเห็นคุณพอดี", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "termite_mound",
    archetypes: S,
    stages: ["adult"],
    requires: { families: ["Termitidae"], flags: ["role_worker"] },
    weight: 5,
    text: "ฝนตกหนักกัดเซาะจอมปลวกจนเป็นรู",
    fact: "จอมปลวกมีช่องลมภายในที่ช่วยระบายอากาศและรักษาอุณหภูมิในรังให้ค่อนข้างคงที่",
    choices: [
      { label: "ขนดินผสมน้ำลายมาซ่อม", outcomes: [{ chance: 1, text: "จอมปลวกกลับมาแข็งแรง ห้องอนุบาลของน้องๆ {n} ตัวปลอดภัย", effects: { offspringCount: 5, safety: 10, fullness: -5 } }] },
      {
        label: "เฝ้ารูไว้กันผู้บุกรุก",
        outcomes: [
          { chance: 0.7, text: "ตัวงานตัวอื่นมาซ่อมก่อนมดจะรู้ตัว", effects: { safety: 5 } },
          { chance: 0.3, text: "มดแห่กันเข้ามาทางรู คุณโดนกัด", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "termite_fungus",
    archetypes: S,
    stages: ["adult"],
    requires: { families: ["Termitidae"], flags: ["role_worker"] },
    weight: 4,
    text: "สวนเห็ดในห้องลึกของรังต้องการเศษใบไม้เพิ่ม",
    fact: "ปลวกบางชนิดเพาะเห็ดในรังเพื่อช่วยย่อยอาหาร เห็ดโคนที่คนไทยชอบกินก็งอกขึ้นมาจากรังปลวกพวกนี้",
    choices: [
      { label: "ขนเศษใบไม้มาเลี้ยงเห็ด", outcomes: [{ chance: 1, text: "สวนเห็ดงอกงาม เป็นอาหารให้น้องๆ ได้อีก {n} ตัว", effects: { offspringCount: 10, fullness: -5 } }] },
      { label: "กินเห็ดที่โตแล้ว", outcomes: [{ chance: 1, text: "เห็ดย่อยไม้ไว้ให้แล้ว คุณกินได้อย่างสบาย", effects: { fullness: 30 } }] },
    ],
  },

  // ---------- ผึ้งและต่อ ----------
  {
    id: "bee_waggle",
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: BEE_WASP, families: ["Apidae"], flags: ["role_worker"] },
    weight: 7,
    text: "พี่ผึ้งตัวหนึ่งกลับมาพร้อมเต้นส่ายก้นเป็นวงบนรวง",
    fact: "ผึ้งพันธุ์บอกทิศทางและระยะทางของแหล่งอาหารให้เพื่อนด้วยการเต้นส่ายก้น โดยใช้ดวงอาทิตย์เป็นจุดอ้างอิง",
    choices: [
      { label: "ตามท่าเต้นไปหาดอกไม้", outcomes: [{ chance: 1, text: "คุณเจอดงดอกไม้ตรงตามท่าเต้นเป๊ะ น้ำหวานที่ขนกลับมาเลี้ยงน้องๆ ได้อีก {n} ตัว", effects: { fullness: 25, offspringCount: 10 } }] },
      {
        label: "ออกหาดอกไม้เอง",
        outcomes: [
          { chance: 0.5, text: "คุณเจอดอกไม้ต้นใหม่ที่ยังไม่มีใครรู้", effects: { fullness: 20, offspringCount: 5 } },
          { chance: 0.5, text: "บินวนอยู่นานก็เจอแค่ดอกไม้ที่น้ำหวานหมดแล้ว", effects: { fullness: 5 } },
        ],
      },
    ],
  },
  {
    id: "bee_honey",
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: BEE_WASP, families: ["Apidae"], flags: ["role_worker"] },
    weight: 6,
    text: "หลอดรวงเต็มไปด้วยน้ำหวานที่ยังเหลวอยู่",
    fact: "ผึ้งทำน้ำผึ้งโดยกระพือปีกให้น้ำในน้ำหวานระเหยออก แล้วปิดหลอดรวงด้วยไขผึ้งที่สร้างจากต่อมใต้ท้อง",
    choices: [
      { label: "กระพือปีกพัดลมให้น้ำระเหย", outcomes: [{ chance: 1, text: "น้ำหวานข้นจนกลายเป็นน้ำผึ้ง เสบียงนี้เลี้ยงน้องๆ ได้อีก {n} ตัว", effects: { offspringCount: 8, fullness: -5 } }] },
      { label: "สร้างไขผึ้งปิดฝาหลอด", outcomes: [{ chance: 1, text: "น้ำผึ้งถูกเก็บไว้อย่างดีสำหรับช่วงที่ไม่มีดอกไม้", effects: { offspringCount: 5, safety: 5 } }] },
    ],
  },
  {
    id: "bee_hornet",
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: BEE_WASP, families: ["Apidae"], flags: ["role_worker"] },
    weight: 4,
    text: "ต่อหัวเสือตัวหนึ่งบินวนหน้ารัง คอยจับผึ้งงานไปทีละตัว",
    fact: "ผึ้งโพรงเอเชียรุมล้อมตัวต่อเป็นก้อน แล้วสั่นกล้ามเนื้อจนความร้อนสูงพอจะฆ่าตัวต่อได้",
    choices: [
      {
        label: "รุมล้อมมันเป็นก้อนพร้อมพี่น้อง",
        outcomes: [
          { chance: 0.75, text: "พวกคุณสั่นกล้ามเนื้อจนร้อนจัด ต่อตัวนั้นทนไม่ไหว รังปลอดภัยและน้องๆ {n} ตัวรอด", effects: { safety: 15, offspringCount: 5, fullness: -10 } },
          { chance: 0.25, text: "ต่อกัดคุณขาดก่อนพี่น้องจะล้อมมันได้", effects: { death: "sacrifice" } },
        ],
      },
      {
        label: "หลบเข้าไปในรัง",
        outcomes: [
          { chance: 0.7, text: "ต่อบินวนอยู่พักหนึ่งแล้วจากไป", effects: { safety: -5 } },
          { chance: 0.3, text: "ต่อพุ่งตามมาถึงปากรัง ขาคุณโดนกัด", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "bee_bear",
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: BEE_WASP, flags: ["role_worker"] },
    weight: 3,
    text: "หมีหมาปีนขึ้นมาบนต้นไม้ และเริ่มตะกุยรังเพื่อกินน้ำผึ้งกับตัวอ่อน",
    fact: "เหล็กในของผึ้งงานผึ้งพันธุ์มีเงี่ยง เมื่อต่อยผิวหนังหนาของสัตว์เลี้ยงลูกด้วยนม เหล็กในจะติดค้าง และผึ้งตัวนั้นจะตาย",
    choices: [
      {
        label: "พุ่งเข้าต่อยมัน",
        outcomes: [
          { chance: 0.5, text: "พี่น้องนับร้อยต่อยตาม หมีทนไม่ไหวและปีนลงไป น้องๆ {n} ตัวรอด", effects: { safety: 10, offspringCount: 5 } },
          { chance: 0.5, text: "คุณต่อยหมีสุดแรง และสละชีวิตปกป้องรัง", effects: { death: "sacrifice" } },
        ],
      },
      { label: "อุ้มตัวอ่อนหนีขึ้นไปส่วนบนของรวง", outcomes: [{ chance: 1, text: "รังเสียหายไปส่วนหนึ่ง แต่คุณช่วยน้องๆ ไว้ได้ {n} ตัว", effects: { offspringCount: 3, safety: -10 } }] },
    ],
  },
  {
    id: "wasp_paper",
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: BEE_WASP, families: ["Vespidae"], flags: ["role_worker"] },
    weight: 6,
    text: "รังต้องการห้องใหม่ คุณบินไปหาเนื้อไม้ผุมาเคี้ยว",
    fact: "ต่อและแตนสร้างรังจากเนื้อไม้ที่เคี้ยวผสมน้ำลายจนกลายเป็นกระดาษ",
    choices: [
      { label: "เคี้ยวไม้ไปต่อเติมรัง", outcomes: [{ chance: 1, text: "รังกระดาษขยายใหญ่ขึ้น มีห้องให้น้องๆ อีก {n} ตัว", effects: { offspringCount: 8, fullness: -10 } }] },
      { label: "ล่าหนอนไปเลี้ยงตัวอ่อน", outcomes: [{ chance: 1, text: "คุณเคี้ยวหนอนจนละเอียดแล้วป้อนน้องๆ {n} ตัว", effects: { offspringCount: 10, fullness: 10 } }] },
    ],
  },

  // ---------- นางพญา ----------
  {
    id: "queen_flight",
    opening: true,
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: ANT_TERMITE, flags: ["role_queen"] },
    weight: 10,
    text: "คุณเป็นนางพญาที่มีปีก หลังฝนตกใหญ่ คุณกับนางพญาและตัวผู้จากหลายรังพากันบินขึ้นฟ้าพร้อมกัน",
    fact: "มดและปลวกหลายชนิดบินออกจากรังพร้อมกันเป็นจำนวนมากหลังฝนตก เพื่อให้เจอคู่จากรังอื่นและท่วมนักล่าด้วยจำนวน",
    choices: [
      {
        label: "บินขึ้นสูงหาตัวผู้",
        outcomes: [
          { chance: 0.65, text: "คุณผสมพันธุ์กลางอากาศ แล้วร่อนลงพื้นพร้อมเริ่มชีวิตนางพญา", effects: { fullness: -10, addFlags: ["mated_queen"] } },
          { chance: 0.2, text: "นกนางแอ่นโฉบไล่ คุณหนีลงพื้นทันโดยยังไม่ได้ผสมพันธุ์", effects: { safety: -10 } },
          { chance: 0.15, text: "นกตัวหนึ่งโฉบคุณกลางอากาศ คืนนี้เป็นงานเลี้ยงของนักล่าทุกตัว", effects: { death: "predator" } },
        ],
      },
      {
        label: "บินต่ำใกล้พื้น",
        outcomes: [
          { chance: 0.5, text: "ตัวผู้ตัวหนึ่งบินตามคุณมา คุณผสมพันธุ์สำเร็จ", effects: { addFlags: ["mated_queen"] } },
          { chance: 0.5, text: "คุณร่อนลงพื้นโดยยังไม่เจอตัวผู้", effects: { fullness: -5 } },
        ],
      },
    ],
  },
  {
    id: "termite_alate",
    opening: true,
    archetypes: S,
    stages: ["adult"],
    requires: { families: TERMITE_FAMILIES, flags: ["role_queen"] },
    weight: 10,
    text: "คุณเป็นแมลงเม่า ค่ำนี้หลังฝนแรก คุณกับพี่น้องนับพันบินออกจากรัง",
    fact: "แมลงเม่าคือปลวกที่มีปีก หลังร่อนลงพื้นจะสลัดปีกทิ้ง แล้วราชินีกับราชาปลวกจะช่วยกันสร้างรังและอยู่ด้วยกันไปตลอดชีวิต",
    choices: [
      {
        label: "บินไปหาแสงไฟ",
        outcomes: [
          { chance: 0.4, text: "คุณร่วงลงพื้น สลัดปีกทิ้ง แล้วเจอตัวผู้ที่เดินตามคุณไปติดๆ", effects: { addFlags: ["mated_queen"] } },
          { chance: 0.2, text: "คุณบินวนรอบหลอดไฟจนหมดแรง ยังไม่เจอคู่", effects: { fullness: -15 } },
          { chance: 0.4, text: "กบที่รออยู่ใต้หลอดไฟงับคุณ", effects: { death: "predator" } },
        ],
      },
      {
        label: "ร่อนลงในที่มืด",
        outcomes: [
          { chance: 0.7, text: "คุณสลัดปีกทิ้ง แล้วตัวผู้ตัวหนึ่งก็เดินตามคุณมา คุณได้ราชาแล้ว", effects: { addFlags: ["mated_queen"] } },
          { chance: 0.3, text: "คุณสลัดปีกทิ้ง แต่ยังไม่มีตัวผู้ตามมา", effects: { safety: -5 } },
        ],
      },
    ],
  },
  {
    id: "bee_queen_flight",
    opening: true,
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: BEE_WASP, flags: ["role_queen"] },
    weight: 10,
    text: "คุณเป็นนางพญาตัวใหม่ ถึงเวลาออกบินไปผสมพันธุ์กับตัวผู้จากรังอื่น",
    fact: "นางพญามด ผึ้ง และต่อ ผสมพันธุ์เพียงช่วงสั้นๆ แล้วเก็บสเปิร์มไว้ในตัวใช้วางไข่ได้นานหลายปี",
    choices: [
      {
        label: "บินไปยังจุดที่ตัวผู้รวมตัวกัน",
        outcomes: [
          { chance: 0.8, text: "คุณผสมพันธุ์กับตัวผู้หลายตัวกลางอากาศ แล้วบินกลับมาอย่างปลอดภัย", effects: { fullness: -10, addFlags: ["mated_queen"] } },
          { chance: 0.2, text: "นกจาบคาโฉบคุณกลางอากาศ", effects: { death: "predator" } },
        ],
      },
      {
        label: "รอวันที่อากาศดีกว่านี้",
        outcomes: [
          { chance: 0.6, text: "วันรุ่งขึ้นฟ้าใส คุณออกบินและผสมพันธุ์สำเร็จ", effects: { addFlags: ["mated_queen"] } },
          { chance: 0.4, text: "ฝนตกติดต่อกันหลายวัน คุณยังไม่ได้ออกบิน" },
        ],
      },
    ],
  },
  {
    id: "queen_retry",
    archetypes: S,
    stages: ["adult"],
    requires: { flags: ["role_queen"], notFlags: ["mated_queen"] },
    weight: 30,
    text: "คุณยังไม่ได้ผสมพันธุ์ และเวลาของคุณเหลือไม่มาก",
    choices: [
      {
        label: "ออกบินอีกครั้ง",
        outcomes: [
          { chance: 0.6, text: "ครั้งนี้คุณเจอตัวผู้และผสมพันธุ์สำเร็จ", effects: { fullness: -10, addFlags: ["mated_queen"] } },
          { chance: 0.25, text: "คุณบินจนเหนื่อยแต่ไม่เจอใคร", effects: { fullness: -15 } },
          { chance: 0.15, text: "แมลงปอโฉบคุณกลางอากาศ", effects: { death: "predator" } },
        ],
      },
      {
        label: "หลบรอวันที่ดีกว่า",
        outcomes: [
          { chance: 0.3, text: "ตัวผู้ตัวหนึ่งหลงมาเจอคุณถึงที่ซ่อน", effects: { addFlags: ["mated_queen"] } },
          { chance: 0.7, text: "วันเวลาผ่านไปโดยไม่มีใครมา", effects: { fullness: -5 } },
        ],
      },
    ],
  },
  {
    id: "queen_found_colony",
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: ANT_TERMITE, flags: ["role_queen", "mated_queen"], notFlags: ["colony_founded"] },
    weight: 40,
    text: "ถึงเวลาสร้างอาณาจักรของคุณเอง",
    fact: "นางพญามดหลายชนิดขังตัวเองในโพรง แล้วเลี้ยงลูกรุ่นแรกด้วยพลังงานจากกล้ามเนื้อปีกที่สลายไป โดยไม่ออกไปกินอะไรเลย",
    choices: [
      {
        label: "ขุดโพรงแล้วปิดตัวเองไว้ข้างใน",
        outcomes: [
          { chance: 0.8, text: "คุณอยู่ในความมืดหลายสัปดาห์ เลี้ยงตัวงานรุ่นแรก {n} ตัวด้วยพลังงานในร่างกาย", effects: { offspringCount: 10, fullness: -30, addFlags: ["colony_founded"] } },
          { chance: 0.2, text: "เชื้อราขึ้นในโพรง แต่ลูกๆ {n} ตัวก็ยังรอด", effects: { offspringCount: 3, health: -20, addFlags: ["colony_founded"], cause: "disease" } },
        ],
      },
      {
        label: "หาโพรงใต้ขอนไม้ที่มีอยู่แล้ว",
        outcomes: [
          { chance: 0.6, text: "โพรงนี้แห้งและปลอดภัย ตัวงานรุ่นแรก {n} ตัวฟักออกมาอย่างแข็งแรง", effects: { offspringCount: 8, fullness: -20, addFlags: ["colony_founded"] } },
          { chance: 0.4, text: "ใต้ขอนไม้มีรังมดเจ้าถิ่นอยู่แล้ว มันรุมกัดคุณ", effects: { health: -30, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "bee_queen_colony",
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: BEE_WASP, flags: ["role_queen", "mated_queen"], notFlags: ["colony_founded"] },
    weight: 40,
    text: "ได้เวลาเริ่มต้นรังของคุณแล้ว",
    fact: "เมื่อรังผึ้งพันธุ์แน่นเกินไป นางพญาตัวเก่าจะพาผึ้งงานราวครึ่งรังแยกออกไปหาที่อยู่ใหม่ เรียกว่าการแยกรัง",
    choices: [
      { label: "อยู่รังเดิม ให้นางพญาตัวเก่าแยกรังออกไป", outcomes: [{ chance: 1, text: "นางพญาตัวเก่าพาผึ้งงานครึ่งรังบินออกไป รังนี้กลายเป็นของคุณ", effects: { safety: 10, addFlags: ["colony_founded"] } }] },
      {
        label: "สร้างรังใหม่ด้วยตัวเองตั้งแต่เริ่ม",
        outcomes: [
          { chance: 0.7, text: "คุณสร้างรวงเล็กๆ และเลี้ยงลูกรุ่นแรก {n} ตัวด้วยตัวเอง", effects: { offspringCount: 6, fullness: -30, addFlags: ["colony_founded"] } },
          { chance: 0.3, text: "ลมพายุพัดรังที่เพิ่งเริ่มสร้างพังลง", effects: { health: -20, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "queen_lay",
    archetypes: S,
    stages: ["adult"],
    requires: { flags: ["role_queen", "colony_founded"] },
    weight: 18,
    text: "ในห้องลึกที่สุดของรัง ตัวงานรายล้อมคุณอยู่ตลอดเวลา",
    choices: [
      { label: "วางไข่", outcomes: [{ chance: 1, text: "คุณวางไข่ได้ {n} ฟอง ตัวงานรีบขนไปดูแล", effects: { offspring: "clutch", fullness: -15 } }] },
      { label: "พักให้ตัวงานป้อนอาหารก่อน", outcomes: [{ chance: 1, text: "ตัวงานรุมป้อนอาหารและทำความสะอาดให้คุณ", effects: { fullness: 30, health: 10 } }] },
    ],
  },
  {
    id: "queen_rival",
    archetypes: S,
    stages: ["adult"],
    requires: { flags: ["role_queen", "colony_founded"] },
    weight: 4,
    text: "รังข้างเคียงขยายอาณาเขตเข้ามาจนชนกับรังของคุณ",
    choices: [
      {
        label: "ส่งตัวงานออกไปปกป้องพรมแดน",
        outcomes: [
          { chance: 0.6, text: "ตัวงานของคุณยันศัตรูไว้ได้ รังยังปลอดภัย", effects: { safety: 5 } },
          { chance: 0.4, text: "รังของคุณเสียตัวงานไปมาก ต้องเร่งวางไข่ชดเชย", effects: { fullness: -15, safety: -15 } },
        ],
      },
      { label: "ย้ายห้องของคุณลึกลงไปอีก", outcomes: [{ chance: 1, text: "ศัตรูหาคุณไม่เจอ แต่รังก็เล็กลง", effects: { safety: 10, fullness: -10 } }] },
    ],
  },
  {
    id: "queen_long_life",
    archetypes: S,
    stages: ["adult"],
    requires: { subgroups: ANT_TERMITE, flags: ["role_queen", "colony_founded"] },
    once: true,
    weight: 3,
    text: "ตัวงานรุ่นแรกที่คุณเลี้ยงมาหมดอายุไปหลายรุ่นแล้ว แต่คุณยังอยู่",
    fact: "นางพญามดบางชนิดมีอายุยืนเกือบ 30 ปี ขณะที่ตัวงานอยู่ได้ไม่กี่เดือนถึงไม่กี่ปี",
    choices: [
      { label: "วางไข่ต่อไป", outcomes: [{ chance: 1, text: "ไข่อีก {n} ฟองจะกลายเป็นตัวงานรุ่นใหม่", effects: { offspring: "clutch", fullness: -10 } }] },
    ],
  },
];
